import { jsx } from 'react/jsx-runtime';
import React__default, { useMemo } from 'react';
import { inNextServer } from '@fabric-app/compat';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { viewportBreakpoints } from '../constants/breakpoints.js';

const getBreakpoint = () => {
  if (inNextServer()) {
    return "desktop";
  }
  const width = window.innerWidth;
  if (width <= viewportBreakpoints.XSS) {
    return "xss";
  } else if (width <= viewportBreakpoints.XS) {
    return "xs";
  } else if (width <= viewportBreakpoints.MOBILE) {
    return "mobile";
  } else if (width <= viewportBreakpoints.TABLET) {
    return "tablet";
  } else {
    return "desktop";
  }
};
const ResponsiveContext = /* @__PURE__ */ React__default.createContext({
  breakpoint: "desktop",
  windowWidth: 2e3,
  isMobile: () => false,
  isMobileView: false,
  isTablet: () => false,
  isDesktop: () => true,
  isDesktopView: true,
  isXS: () => false,
  isXSView: false
});
const useResponsive = (options) => {
  const context = React__default.useContext(ResponsiveContext);
  const { isMobileView, isTabletView, isDesktopView, windowWidth } = context;
  const onWidthChangeRef = React__default.useRef(options?.onWidthChange);
  onWidthChangeRef.current = options?.onWidthChange;
  useClientLayoutEffect(() => {
    if (onWidthChangeRef.current) {
      onWidthChangeRef.current({
        isMobileView,
        isTabletView,
        isDesktopView,
        windowWidth
      });
    }
  }, [
    isMobileView,
    isTabletView,
    isDesktopView,
    windowWidth
  ]);
  return context;
};
const ResponsiveProvider = ({ children }) => {
  const [breakpoint, setBreakpoint] = React__default.useState(getBreakpoint());
  const isMobile = React__default.useCallback(() => breakpoint === "mobile" || breakpoint === "xs" || breakpoint === "xss", [
    breakpoint
  ]);
  const isTablet = React__default.useCallback(() => breakpoint === "tablet", [
    breakpoint
  ]);
  const isDesktop = React__default.useCallback(() => breakpoint === "desktop", [
    breakpoint
  ]);
  const isXS = React__default.useCallback(() => breakpoint === "xs", [
    breakpoint
  ]);
  const [windowWidth, setWindowWidth] = React__default.useState(2e3);
  useClientLayoutEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setBreakpoint(getBreakpoint());
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const data = useMemo(() => ({
    breakpoint,
    windowWidth,
    /**
    * @deprecated use isMobileView instead
    */
    isMobile,
    isMobileView: breakpoint === "mobile" || breakpoint === "xs" || breakpoint === "xss",
    /**
    * @deprecated use isTabletView instead
    */
    isTablet,
    isTabletView: breakpoint === "tablet",
    /**
    * @deprecated use isDesktopView instead
    */
    isDesktop,
    isDesktopView: breakpoint === "desktop",
    isXS,
    isXSView: breakpoint === "xs" || breakpoint === "xss",
    isXSSView: breakpoint === "xss"
  }), [
    breakpoint,
    windowWidth,
    isMobile,
    isTablet,
    isDesktop,
    isXS
  ]);
  return /* @__PURE__ */ jsx(ResponsiveContext.Provider, {
    value: data,
    children
  });
};

export { ResponsiveProvider, useResponsive };
//# sourceMappingURL=useResponsive.js.map
